import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiX, FiCheck, FiPlus, FiDollarSign, FiActivity, FiBox,
  FiInfo, FiLayers, FiTag, FiTrendingUp, FiShield,
  FiCpu, FiDatabase, FiZap, FiAlertTriangle, FiCheckCircle,
  FiChevronRight, FiSettings, FiBarChart2, FiRefreshCw,
  FiArrowRight, FiStar, FiThumbsUp, FiThumbsDown
} from 'react-icons/fi';
import { useSolution } from '../../context/SolutionContext';
import { getAlternativesForComponent, getBestAlternative, getComponentAlternativesCount } from '../../data/componentAlternatives';

const ComponentDetail = () => {
  const { state, dispatch } = useSolution();
  const component = state.uiState?.activeComponent;
  const [showAlternatives, setShowAlternatives] = useState(true);
  const [selectedAlternative, setSelectedAlternative] = useState(null);
  const [showComparison, setShowComparison] = useState(false);
  
  // Get alternatives for this component - MOVED BEFORE EARLY RETURN
  const alternatives = useMemo(() => {
    if (!component) return [];
    return getAlternativesForComponent(component.name);
  }, [component]);
  
  const bestAlternative = useMemo(() => {
    if (!component) return null;
    return getBestAlternative(component.name, state.approachConstraints || {});
  }, [component, state.approachConstraints]);
  
  // Early return AFTER all hooks
  if (!component) return null;
  
  const isSelected = state.selectedComponents?.some(c => c.id === component.id) || false;
  const quantity = state.selectedComponents?.find(c => c.id === component.id)?.quantity || 0;
  const specifications = component.specifications || component.sizingInfo?.specifications || null;
  
  const alternativesCount = alternatives.length;
  
  const handleToggle = () => {
    if (isSelected) {
      dispatch({ type: 'REMOVE_COMPONENT', payload: component });
    } else {
      dispatch({ type: 'ADD_COMPONENT', payload: component });
    }
  };
  
  const handleClose = () => {
    dispatch({ type: 'HIDE_COMPONENT_DETAIL' });
  };
  
  const handleReplaceWithAlternative = (alternative) => {
    // Remove current component
    dispatch({ type: 'REMOVE_COMPONENT', payload: component });
    
    // Add alternative as new component
    const alternativeComponent = {
      ...component,
      id: `alt-${component.id}-${alternative.id}`,
      name: alternative.name,
      icon: alternative.icon,
      purpose: alternative.description || component.purpose,
      function: alternative.description || component.function,
      specifications: alternative.specifications,
      isAlternative: true,
      originalComponent: component.name,
      alternativeReason: `Replaced ${component.name} with ${alternative.name}`,
      cost: alternative.specifications?.costRange || component.cost
    };
    
    dispatch({ type: 'ADD_COMPONENT', payload: alternativeComponent });
    dispatch({ 
      type: 'SHOW_TOAST', 
      payload: { type: 'success', message: `Replaced ${component.name} with ${alternative.name}` } 
    });
    
    setSelectedAlternative(null);
  };
  
  const handleSelectAlternative = (alternative) => {
    setSelectedAlternative(selectedAlternative?.id === alternative.id ? null : alternative);
  };
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex justify-end"
        onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.3 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black"
          onClick={handleClose}
        />
        
        <motion.div
          initial={{ x: 500, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: 500, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="relative w-full max-w-[600px] h-full bg-white shadow-2xl overflow-y-auto z-[101]"
        >
          {/* Sticky Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 p-5 z-10 shadow-sm">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <span className="text-3xl">{component.icon}</span>
                <div>
                  <h2 className="text-lg font-bold text-gray-900 leading-tight">{component.name}</h2>
                  <p className="text-xs text-gray-500">
                    {component.layerName || ''} → {component.subLayerName || ''}
                  </p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="p-2 bg-gray-100 hover:bg-red-100 text-gray-600 hover:text-red-600 rounded-full transition-colors flex-shrink-0"
                title="Close (Esc)"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>
            
            {/* Alternatives Badge */}
            {alternativesCount > 0 && (
              <div className="mt-3 flex items-center gap-2">
                <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-semibold flex items-center gap-1">
                  <FiRefreshCw className="w-3 h-3" />
                  {alternativesCount} Alternatives Available
                </span>
                {bestAlternative && (
                  <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold flex items-center gap-1">
                    <FiStar className="w-3 h-3" />
                    Best: {bestAlternative.name}
                  </span>
                )}
              </div>
            )}
          </div>
          
          <div className="p-5 space-y-5">
            {/* Basic Info */}
            <div className="space-y-3">
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-1">Purpose</h3>
                <p className="text-gray-600 text-sm">{component.purpose || 'N/A'}</p>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-1">Function</h3>
                <p className="text-gray-600 text-sm">{component.function || 'N/A'}</p>
              </div>
            </div>
            
            {/* ==================== COMPONENT ALTERNATIVES SECTION ==================== */}
            {alternativesCount > 0 && (
              <div className="bg-purple-50 rounded-xl border border-purple-200 overflow-hidden">
                <button
                  onClick={() => setShowAlternatives(!showAlternatives)}
                  className="w-full p-3 bg-purple-100 flex items-center justify-between"
                >
                  <h3 className="text-sm font-bold text-purple-800 flex items-center gap-2">
                    <FiRefreshCw className="w-4 h-4" />
                    Component Alternatives ({alternativesCount})
                  </h3>
                  {showAlternatives ? <FiChevronRight className="w-4 h-4 rotate-90" /> : <FiChevronRight className="w-4 h-4" />}
                </button>
                
                <AnimatePresence>
                  {showAlternatives && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="p-4 space-y-3">
                        {/* Best Alternative Highlight */}
                        {bestAlternative && (
                          <div className="p-3 bg-green-50 border-2 border-green-300 rounded-lg">
                            <div className="flex items-center gap-2 mb-1">
                              <FiStar className="w-4 h-4 text-yellow-500" />
                              <span className="text-xs font-bold text-green-800">RECOMMENDED ALTERNATIVE</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <span className="text-2xl">{bestAlternative.icon}</span>
                                <div>
                                  <div className="font-semibold text-gray-900 text-sm">{bestAlternative.name}</div>
                                  <div className="text-xs text-gray-500">{bestAlternative.description}</div>
                                </div>
                              </div>
                              <button
                                onClick={() => handleReplaceWithAlternative(bestAlternative)}
                                className="px-3 py-1.5 bg-green-500 text-white rounded-lg text-xs font-semibold hover:bg-green-600 flex items-center gap-1"
                              >
                                <FiArrowRight className="w-3 h-3" />
                                Replace
                              </button>
                            </div>
                          </div>
                        )}
                        
                        {/* All Alternatives */}
                        {alternatives.map(alternative => (
                          <div
                            key={alternative.id}
                            className={`p-3 rounded-lg border-2 transition-all cursor-pointer ${
                              selectedAlternative?.id === alternative.id
                                ? 'border-purple-400 bg-purple-50'
                                : 'border-gray-200 bg-white hover:border-purple-200'
                            }`}
                            onClick={() => handleSelectAlternative(alternative)}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <span className="text-2xl">{alternative.icon}</span>
                                <div>
                                  <div className="font-semibold text-gray-900 text-sm">{alternative.name}</div>
                                  <div className="text-xs text-gray-500">{alternative.description}</div>
                                </div>
                              </div>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleReplaceWithAlternative(alternative);
                                }}
                                className="px-3 py-1.5 bg-purple-500 text-white rounded-lg text-xs font-semibold hover:bg-purple-600"
                              >
                                Replace
                              </button>
                            </div>
                            
                            {/* Expanded Details */}
                            {selectedAlternative?.id === alternative.id && (
                              <div className="mt-3 space-y-2">
                                {/* Specifications */}
                                {alternative.specifications && (
                                  <div className="bg-white rounded-lg p-3 border border-gray-100">
                                    <h4 className="text-xs font-bold text-gray-700 mb-2">Specifications</h4>
                                    {Object.entries(alternative.specifications).map(([key, value]) => {
                                      if (typeof value === 'object') return null;
                                      return (
                                        <div key={key} className="flex justify-between text-xs">
                                          <span className="text-gray-500 capitalize">{key.replace(/([A-Z])/g, ' $1')}</span>
                                          <span className="font-medium text-gray-900">{String(value)}</span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                                
                                {/* Advantages */}
                                {alternative.advantages && (
                                  <div>
                                    <h4 className="text-xs font-bold text-green-700 mb-1">Advantages</h4>
                                    <ul className="space-y-1">
                                      {alternative.advantages.map((adv, i) => (
                                        <li key={i} className="flex items-center gap-2 text-xs text-gray-600">
                                          <FiThumbsUp className="w-3 h-3 text-green-500 flex-shrink-0" />
                                          {adv}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                                
                                {/* Disadvantages */}
                                {alternative.disadvantages && (
                                  <div>
                                    <h4 className="text-xs font-bold text-red-700 mb-1">Disadvantages</h4>
                                    <ul className="space-y-1">
                                      {alternative.disadvantages.map((dis, i) => (
                                        <li key={i} className="flex items-center gap-2 text-xs text-gray-600">
                                          <FiThumbsDown className="w-3 h-3 text-red-500 flex-shrink-0" />
                                          {dis}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                                
                                {/* Cost */}
                                {alternative.specifications?.costRange && (
                                  <div className="flex items-center gap-2 text-xs">
                                    <FiDollarSign className="w-3 h-3 text-green-600" />
                                    <span className="font-semibold text-green-700">
                                      ${alternative.specifications.costRange.low} - ${alternative.specifications.costRange.high} {alternative.specifications.costRange.unit}
                                    </span>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
            
            {/* Component Specifications */}
            {specifications && (
              <div className="bg-blue-50 rounded-xl border border-blue-200 overflow-hidden">
                <div className="p-3 bg-blue-100 border-b border-blue-200">
                  <h3 className="text-sm font-bold text-blue-800 flex items-center gap-2">
                    <FiSettings className="w-4 h-4" />
                    Component Specifications
                  </h3>
                </div>
                <div className="p-4 space-y-2">
                  {Object.entries(specifications).map(([key, value]) => {
                    if (key === 'costRange' || value === null || value === undefined) return null;
                    return (
                      <div key={key} className="flex items-start justify-between gap-4">
                        <span className="text-xs font-medium text-gray-600 capitalize">
                          {key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim()}
                        </span>
                        <span className="text-xs font-semibold text-gray-900 text-right">
                          {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* Cost */}
            {component.cost && (
              <div className="bg-gray-50 rounded-xl p-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Cost</h3>
                <div className="flex items-center gap-2 text-lg font-bold text-green-600">
                  <FiDollarSign className="w-5 h-5" />
                  <span>${component.cost.low || 0} - ${component.cost.high || 0}</span>
                  <span className="text-sm font-normal text-gray-500">{component.cost.unit || ''}</span>
                </div>
              </div>
            )}
            
            {/* Action Buttons */}
            <div className="flex gap-3 pt-2 border-t border-gray-100">
              <button
                onClick={handleToggle}
                className={`flex-1 py-3 rounded-xl font-bold text-white transition-all ${
                  isSelected ? 'bg-red-500 hover:bg-red-600' : 'bg-green-500 hover:bg-green-600'
                }`}
              >
                {isSelected ? `Remove (Qty: ${quantity})` : 'Add to Solution'}
              </button>
              <button
                onClick={handleClose}
                className="px-6 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 font-semibold transition-all"
              >
                Close
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ComponentDetail;